/**
 * FilePreviewModal.tsx
 * 
 * A modal component for previewing file content, including images, PDFs, and other file types.
 * Provides a larger view of files when clicked in messages.
 */

import { useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileAttachment } from '@/types/posit';
import { Download, File, FileText, Image, FileSpreadsheet } from 'lucide-react';
import { useBreakpoint } from '@/components/ui/responsive';

interface FilePreviewModalProps {
  file: FileAttachment | null;
  isOpen: boolean;
  onClose: () => void;
}

const FilePreviewModal = ({ file, isOpen, onClose }: FilePreviewModalProps) => {
  const { isMobile, isTablet } = useBreakpoint();

  // Clean up when the component unmounts or when the file changes
  useEffect(() => {
    return () => {
      // No need to explicitly revoke URLs here as they're tracked by the fileUpload utility
      // and will be cleaned up when the file is removed from the attachedFiles state
    };
  }, [file]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  if (!file) return null;

  const getFileIcon = () => {
    if (file.mimeType.startsWith('image/')) {
      return <Image className="h-10 w-10 text-blue-500" />;
    } else if (file.mimeType === 'application/pdf') {
      return <FileText className="h-10 w-10 text-red-500" />;
    } else if (file.mimeType.includes('spreadsheet') || file.mimeType.includes('excel') || file.mimeType.includes('csv')) {
      return <FileSpreadsheet className="h-10 w-10 text-green-500" />;
    } else {
      return <File className="h-10 w-10 text-gray-500" />;
    }
  };

  const renderFileContent = () => {
    const mobileImageHeight = isMobile ? 'max-h-[50vh]' : 'max-h-[70vh]';
    const mobilePdfHeight = isMobile ? 'h-[50vh]' : 'h-[70vh]';
    const mobilePadding = isMobile ? 'p-2' : 'p-4';

    if (file.mimeType.startsWith('image/') && file.url) {
      return (
        <div className={`flex justify-center ${mobilePadding}`} role="img" aria-label={`Image preview of ${file.name}`}>
          <img 
            src={file.url} 
            alt={`Preview of ${file.name}`}
            className={`max-w-full ${mobileImageHeight} object-contain rounded-md`}
            loading="lazy"
          />
        </div>
      );
    } else if (file.mimeType === 'application/pdf' && file.url) {
      return (
        <div className={`${mobilePdfHeight} ${mobilePadding}`} role="document" aria-label={`PDF preview of ${file.name}`}>
          <iframe 
            src={file.url} 
            className="w-full h-full border-0 rounded-md"
            title={`PDF preview of ${file.name}`}
            aria-label={`PDF content of ${file.name}`}
          />
        </div>
      );
    } else {
      return (
        <div className={`flex flex-col items-center justify-center ${isMobile ? 'p-4' : 'p-8'}`} role="region" aria-label="File preview unavailable">
          <div className={`${isMobile ? 'text-2xl' : 'text-4xl'} mb-4`} aria-hidden="true">{getFileIcon()}</div>
          <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-medium mb-2`} id="file-name">{file.name}</h3>
          <p className={`text-muted-foreground mb-4 ${isMobile ? 'text-sm text-center' : ''}`} aria-describedby="file-name">
            Preview not available for this file type
          </p>
          {file.url && (
            <Button 
              asChild 
              variant="outline"
              size={isMobile ? 'sm' : 'default'}
              aria-label={`Download ${file.name}`}
            >
              <a href={file.url} download target="_blank" rel="noopener noreferrer">
                <Download className="mr-2 h-4 w-4" aria-hidden="true" />
                Download File
              </a>
            </Button>
          )}
        </div>
      );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className={`${isMobile ? 'max-w-[95vw] w-[95vw] h-[90vh] max-h-[90vh]' : 'max-w-4xl w-[90vw]'} ${isMobile ? 'p-3' : ''}`}
        aria-describedby="file-preview-description"
      >
        <DialogHeader className={isMobile ? 'pb-2' : ''}>
          <DialogTitle className={`flex items-center gap-2 ${isMobile ? 'text-base' : ''}`}>
            <span aria-hidden="true">{getFileIcon()}</span>
            <span className={isMobile ? 'truncate' : ''}>{file.name}</span>
          </DialogTitle>
          <p id="file-preview-description" className="sr-only">
            File preview modal for {file.name}. Press Escape to close.
          </p>
        </DialogHeader>
        <div 
          role="main" 
          aria-label="File preview content"
          className={isMobile ? 'flex-1 overflow-hidden' : ''}
        >
          {renderFileContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FilePreviewModal; 